import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner, Alert, Button, Card, Badge, Form } from 'react-bootstrap';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  getBlueVerificationStatus,
  requestBlueVerification,
  cancelBlueVerification,
  registerUserToBlueTick
} from '../../api/profile';
import { fetchProfile } from '../../store/slices/profileSlice';

export const BlueVerificationTab = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // File Upload State
  const [documentFile, setDocumentFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getBlueVerificationStatus();
      if (res && res.data) {
        setStatusData(res.data);
      }
    } catch (err) {
      console.error('Error fetching verification status:', err);
      setError(err.response?.data?.message || 'Failed to retrieve verification status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setDocumentFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setDocumentFile(e.target.files[0]);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!documentFile) {
      setError('Please select a supporting document first.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);

      const formData = new FormData();
      formData.append('documents', documentFile);

      const res = await requestBlueVerification(formData);
      setSuccessMsg(res.message || 'Verification request submitted successfully!');
      setDocumentFile(null);
      await fetchStatus();
    } catch (err) {
      console.error('Error submitting verification request:', err);
      setError(err.response?.data?.message || 'Failed to submit verification request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (chargeId) => {
    try {
      setLoading(true);
      setError(null);
      const res = await registerUserToBlueTick(chargeId);
      setSuccessMsg(res.message || 'Blue Tick subscription activated successfully!');

      // Update global user profile state to show verified badge immediately
      dispatch(fetchProfile());
      await fetchStatus();
    } catch (err) {
      console.error('Error registering blue tick:', err);
      setError(err.response?.data?.message || 'Payment was successful, but activation failed. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Blue Verification subscription? Your badge will be removed.')) {
      return;
    }

    try {
      setCancelling(true);
      setError(null);
      setSuccessMsg(null);
      const res = await cancelBlueVerification();
      setSuccessMsg(res.message || 'Subscription cancelled successfully.');

      // Update global user profile state to remove verified badge immediately
      dispatch(fetchProfile());
      await fetchStatus();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.message || 'Failed to cancel subscription.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReapply = () => {
    // Reset view so the user can submit a new request
    setStatusData({
      has_request: false,
      can_request: true
    });
    setDocumentFile(null);
    setError(null);
    setSuccessMsg(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Loading verification details...</p>
      </div>
    );
  }

  // Determine current verification state from statusData
  const hasRequest = statusData?.has_request;
  const canRequest = statusData?.can_request;
  const status = statusData?.status; // 'pending' | 'approved' | 'rejected'
  const isActive = statusData?.is_active || statusData?.is_blue_verified;
  const monthlyFee = statusData?.monthly_fee || 9.99;
  const rejectionReason = statusData?.rejection_reason;

  return (
    <div className="blue-verification-container">
      {successMsg && (
        <Alert variant="success" onClose={() => setSuccessMsg(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-check-circle-fill me-2"></i>{successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
        </Alert>
      )}

      {/* Case 1: Active Subscription */}
      {isActive && (
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4 premium-active-card">
          <div className="gradient-header p-4 text-white text-center position-relative">
            <div className="sparkles-bg"></div>
            <div className="badge-glowing-wrapper mb-3">
              <i className="bi bi-patch-check-fill glow-icon"></i>
            </div>
            <h3 className="fw-black m-0">Verification Active</h3>
            <p className="text-white-50 mt-1 mb-0">You are a verified member of the YooSpeak community</p>
          </div>
          <Card.Body className="p-4 bg-white">
            <div className="d-flex flex-column align-items-center mb-4">
              <Badge bg="success" className="px-3 py-2 rounded-pill mb-3">
                <i className="bi bi-check-circle-fill me-1"></i>Active Subscription
              </Badge>
              <div className="subscription-details text-center">
                <div className="detail-item mb-2">
                  <span className="text-muted text-sm d-block">Monthly Cost</span>
                  <span className="fw-bold fs-5 text-gray-900">${monthlyFee} USD</span>
                </div>
                {statusData?.subscription_end_date && (
                  <div className="detail-item">
                    <span className="text-muted text-sm d-block">Next Renewal Date</span>
                    <span className="fw-semibold text-gray-800">
                      {new Date(statusData.subscription_end_date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="info-notes mb-4 p-3 rounded-3 bg-light border-start border-4 border-info">
              <h6 className="fw-bold text-info-emphasis mb-1"><i className="bi bi-info-circle me-1"></i>Benefits Active:</h6>
              <ul className="text-sm text-muted m-0 ps-3">
                <li>Blue check badge on your profile and interactions.</li>
                <li>Enhanced visibility across the search directory.</li>
                <li>Priority support queue.</li>
              </ul>
            </div>

            <div className="text-center">
              <Button
                variant="outline-danger"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="px-4 py-2 rounded-pill fw-bold"
              >
                {cancelling ? <><Spinner animation="border" size="sm" className="me-2" />Cancelling...</> : 'Cancel Subscription'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Case 2: Approved but unpaid */}
      {status === 'approved' && !isActive && (
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
          <div className="bg-success bg-opacity-10 p-4 text-center border-bottom border-success border-opacity-10">
            <div className="badge-glowing-wrapper mb-3 text-success">
              <i className="bi bi-patch-check-fill fs-1"></i>
            </div>
            <h3 className="fw-black text-success mb-1">Request Approved!</h3>
            <p className="text-muted m-0">Complete your payment to activate your Blue Verification badge</p>
          </div>
          <Card.Body className="p-4 bg-white">
            <div className="text-center mb-4">
              <div className="price-display py-3">
                <span className="fs-3 fw-black text-gray-900">${monthlyFee}</span>
                <span className="text-muted text-sm"> / month</span>
              </div>
              <p className="text-sm text-muted px-md-5">
                Keep your profile verified and build credibility in the YooSpeak marketplace. You will be billed monthly. You can cancel at any time.
              </p>
            </div>

            <div className="paypal-btn-container mx-auto" style={{ maxWidth: '400px' }}>
              <PayPalScriptProvider options={{ "client-id": "AcEBlYxSTDKF51XKtEB_L4-X18Q4kpoyUj0yY6DggYqVCssqh9nJbcUESyx6Fp_HLJnPy3msNxmjttaG", currency: "USD" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay" }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: { value: monthlyFee.toString() },
                        description: "YooSpeak Blue Verification Monthly Subscription"
                      }]
                    });
                  }}
                  onApprove={(data, actions) => {
                    return actions.order.capture().then(async (details) => {
                      await handlePaymentSuccess(details.id);
                    });
                  }}
                  onError={(err) => {
                    console.error('PayPal Payment Error:', err);
                    setError('An error occurred during payment processing. Please try again.');
                  }}
                />
              </PayPalScriptProvider>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Case 3: Pending review */}
      {status === 'pending' && (
        <Card className="border-0 shadow-sm rounded-4 p-5 text-center bg-white mb-4">
          <div className="pending-icon-wrapper mb-4">
            <div className="icon-pulse bg-warning bg-opacity-10 text-warning rounded-circle d-inline-flex p-4">
              <i className="bi bi-hourglass-split fs-1"></i>
            </div>
          </div>
          <h3 className="fw-black text-gray-900 mb-2">Request Under Review</h3>
          <p className="text-muted mx-auto" style={{ maxWidth: '500px' }}>
            We've received your documents and are currently verifying your identity. This process typically takes between 24 to 48 hours. We'll update your status here once review is complete.
          </p>
          {statusData?.submitted_at && (
            <div className="text-xs text-muted mt-3">
              Submitted on: {new Date(statusData.submitted_at).toLocaleString()}
            </div>
          )}
        </Card>
      )}

      {/* Case 4: Rejected */}
      {status === 'rejected' && (
        <Card className="border-0 shadow-sm rounded-4 p-5 text-center bg-white mb-4 border-top border-5 border-danger">
          <div className="rejected-icon-wrapper mb-4">
            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-4">
              <i className="bi bi-x-circle-fill fs-1"></i>
            </div>
          </div>
          <h3 className="fw-black text-gray-900 mb-2">Verification Rejected</h3>
          <p className="text-muted mx-auto" style={{ maxWidth: '500px' }}>
            Unfortunately, your verification request could not be approved at this time.
          </p>

          {rejectionReason && (
            <Alert variant="danger" className="mx-auto rounded-3 my-4 text-start" style={{ maxWidth: '500px' }}>
              <h6 className="fw-bold mb-1"><i className="bi bi-info-circle me-1"></i>Feedback:</h6>
              <p className="text-sm m-0">{rejectionReason}</p>
            </Alert>
          )}

          <div className="mt-4">
            <Button variant="primary" onClick={handleReapply} className="px-5 py-2.5 rounded-pill fw-bold">
              Submit a New Request
            </Button>
          </div>
        </Card>
      )}

      {/* Case 5: No Request / Can Request */}
      {canRequest && !hasRequest && !isActive && (
        <div className="request-verification-section">
          {/* Header Showcase */}
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4 hero-showcase-card">
            <div className="gradient-header-blue p-5 text-white position-relative">
              <div className="circles-bg"></div>
              <div className="d-flex flex-column flex-md-row align-items-center gap-4 position-relative z-1">
                <div className="badge-showcase mb-3 mb-md-0">
                  <i className="bi bi-patch-check-fill big-check"></i>
                </div>
                <div className="text-center text-md-start">
                  <h2 className="fw-black text-white m-0">Get Your Blue Badge</h2>
                  <p className="text-white-85 mt-1 mb-0 fs-5">Enhance your credibility and build trust with clients on YooSpeak</p>
                </div>
              </div>
            </div>

            <Card.Body className="p-4 bg-white">
              <h5 className="fw-bold text-gray-900 mb-4">Why get verified?</h5>
              <div className="row g-4 mb-4">
                {[
                  { title: "Build Premium Trust", desc: "Showcases that your profile is authentic, increasing bookings and user confidence.", icon: "bi-shield-check", color: "primary" },
                  { title: "Stand Out In Search", desc: "Gain search priority and highlighted listing to gain higher visibility.", icon: "bi-graph-up-arrow", color: "info" },
                  { title: "Premium Support", desc: "Verified users receive fast-track service and dedicated support.", icon: "bi-headset", color: "success" }
                ].map((item, idx) => (
                  <div key={idx} className="col-md-4">
                    <div className="feature-item p-3 rounded-3 h-100 border">
                      <div className={`icon-box text-${item.color} bg-${item.color} bg-opacity-10 rounded-3 mb-3 d-inline-flex p-2.5`}>
                        <i className={`bi ${item.icon} fs-4`}></i>
                      </div>
                      <h6 className="fw-bold text-gray-900 mb-1">{item.title}</h6>
                      <p className="text-sm text-muted m-0">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Form Section */}
              <div className="border-top pt-4">
                <h5 className="fw-bold text-gray-900 mb-3">Submit Verification Application</h5>
                <Form onSubmit={handleSubmitRequest}>
                  <div className="mb-4">
                    <Form.Label className="text-gray-700 fw-semibold mb-2">Upload Supporting Document</Form.Label>
                    <p className="text-xs text-muted mb-3">
                      Please upload a copy of a government-issued ID (Passport, National ID Card, Driver's License) or a professional credential. File formats accepted: PDF, JPG, PNG. (Max 10MB)
                    </p>

                    <div
                      className={`upload-dropzone p-5 text-center rounded-3 border-2 border-dashed ${dragActive ? 'drag-active' : ''} ${documentFile ? 'has-file' : ''}`}
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="document-upload-input"
                        className="d-none"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />

                      {!documentFile ? (
                        <div className="dropzone-content">
                          <i className="bi bi-cloud-upload fs-1 text-primary opacity-50 mb-3 d-block"></i>
                          <p className="fw-medium text-gray-800 m-0">Drag & drop your file here, or <label htmlFor="document-upload-input" className="text-primary text-decoration-underline cursor-pointer">browse</label></p>
                          <span className="text-xs text-muted">Supports PDF, JPG, PNG up to 10MB</span>
                        </div>
                      ) : (
                        <div className="dropzone-content has-file-content d-flex align-items-center justify-content-center gap-3">
                          <i className="bi bi-file-earmark-check fs-2 text-success"></i>
                          <div className="text-start">
                            <div className="fw-bold text-gray-900 text-sm text-truncate" style={{ maxWidth: '250px' }}>{documentFile.name}</div>
                            <div className="text-xs text-muted">{(documentFile.size / (1024 * 1024)).toFixed(2)} MB</div>
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-circle p-1 flex-center border-0 ms-2"
                            style={{ width: '28px', height: '28px' }}
                            onClick={() => setDocumentFile(null)}
                          >
                            <i className="bi bi-x fs-5"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting || !documentFile}
                      className="px-5 py-2.5 rounded-pill fw-bold shadow-sm"
                    >
                      {submitting ? <><Spinner animation="border" size="sm" className="me-2" />Submitting...</> : 'Submit Application'}
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};
